import {Box} from '@mui/material';
import {useState} from 'react';
import {Outlet} from 'react-router';
import {PublicSideNavigation} from '../../navigation/PublicSideNavigation';
import {ComponentWithoutChildren} from '../../react/types';
import {useDeviceState} from '../../responsive/hooks/useDeviceState';
import {Content, ContentProps} from '../Content';
import {LayoutOffsetContext} from '../hooks/useLayoutOffset';
import {useOffsetHeight} from '../hooks/useOffsetHeight';
import {PublicTopBar, PublicTopBarProps} from './PublicTopBar';

export interface PublicLayoutProps {
  contentProps?: ContentProps;
  publicTopBarProps?: Omit<PublicTopBarProps, 'handleNavigationMenuClick'>;
  withoutContent?: boolean;
}

export const PublicLayout: ComponentWithoutChildren<PublicLayoutProps> = ({
  contentProps,
  publicTopBarProps,
  withoutContent = false,
}: PublicLayoutProps) => {
  /**
   * Dynamically compute the height/width of the major components.
   * These values will be used to align the components relative to each other in the layout.
   */
  const {offsetHeight: topBarHeight = 0, ref: topBarRef} = useOffsetHeight<HTMLDivElement>();

  const {isNarrow} = useDeviceState();
  const [navigationOpen, setNavigationOpen] = useState<boolean>(false);
  const toggleNavigationMenuOpen = () => setNavigationOpen(!navigationOpen);

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
      <PublicTopBar handleNavigationMenuClick={toggleNavigationMenuOpen} ref={topBarRef} {...publicTopBarProps} />
      <LayoutOffsetContext value={{horizontalOffset: 0, verticalOffset: topBarHeight}}>
        {isNarrow && <PublicSideNavigation isOpen={navigationOpen} onClose={() => setNavigationOpen(false)} />}
        {withoutContent ? (
          <main role="main">
            <Outlet />
          </main>
        ) : (
          <Content {...contentProps} />
        )}
      </LayoutOffsetContext>
    </Box>
  );
};

export default PublicLayout; // Default export required for react lazy
